
'use client';

import AnsweringServiceVsGreet from '@/components/AnsweringServiceVsGreet';
import LandingLayout from '@/components/LandingLayout';
import { landscapersFaqs } from '@/data/industryFaqs';
import { trackButtonClick, trackPageView, trackPhoneClick } from '@/lib/analytics';
import {
  ArrowRightIcon,
  CalendarIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ClockIcon,
  DocumentTextIcon,
  MapPinIcon,
  PhoneIcon,
  StarIcon,
  UserGroupIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContextNext';
import { getPlanDisplayPrice, PRICING_PLANS, PUBLIC_SALE_STARTING_PRICE } from '../../types/pricing';

export default function LandscapersPage() {
  useEffect(() => {
    trackPageView('Landscapers Landing', undefined, { funnel_step: 'industry_landing' });
  }, []);

  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const { isAuthenticated, isInitialized } = useAuth();
  const pricingContext = { isAuthenticated: !isInitialized || isAuthenticated };
  const planDisplayPrices = PRICING_PLANS.map((plan) => getPlanDisplayPrice(plan, pricingContext));
  const priceRangeDisplay = planDisplayPrices.some((displayPrice) => displayPrice.showSale)
    ? '$29.97-$119.97/month'
    : '$49.95-$199.95/month';

  const painPoints = [
    {
      problem: "Missed calls during peak season",
      impact: "Lost $5,000-15,000+ per week in quotes and bookings"
    },
    {
      problem: "Stuck answering 'How much?' 30+ times daily",
      impact: "5-8 hours weekly on repetitive questions"
    },
    {
      problem: "Phone ringing while hands are dirty",
      impact: "Can't answer calls mid-job, customers call competitors"
    },
    {
      problem: "Evening/weekend calls interrupt family time",
      impact: "No real work-life balance during busy seasons"
    }
  ];

  const landscaperBenefits = [
    {
      title: "Answer 100% of Calls",
      description: "Even when you're elbow-deep in mulch or on another job, every caller gets instant professional response",
      icon: PhoneIcon,
      stat: "100%"
    },
    {
      title: "Save 8-12 Hours Weekly",
      description: "Stop answering the same 50 questions about pricing, services, and availability",
      icon: ClockIcon,
      stat: "8-12 hrs"
    },
    {
      title: "Book Appointments While You Work",
      description: "AI schedules consultations directly into your calendar, no back-and-forth needed",
      icon: CalendarIcon,
      stat: "Auto"
    },
    {
      title: "Capture Emergency Work",
      description: "Never miss storm damage or urgent tree removal calls-highest paying work gets prioritized",
      icon: MapPinIcon,
      stat: "100%"
    },
    {
      title: "Get Your Evenings Back",
      description: "AI handles after-hours calls professionally while you're with family",
      icon: UserGroupIcon,
      stat: "24/7"
    },
    {
      title: "Detailed Call Summaries",
      description: "Know who called, what they want, and what follow-up is needed before you even respond",
      icon: DocumentTextIcon,
      stat: "Real-time"
    }
  ];

  const testimonials = [
    {
      name: "Tom Richardson",
      business: "Richardson Landscaping & Design",
      stars: 5,
      quote: "We were missing 15-20 calls per week during spring rush. Now we answer everything. Greet books our consultations, and I only get called for actual emergencies. Gained back my weekends.",
      metric: "300+ new leads captured"
    },
    {
      name: "Sarah Mitchell",
      business: "Bloom & Grow Landscapes",
      stars: 5,
      quote: "As a female-owned landscape company, we wanted to project professionalism. Greet sounds like we have a full office staff. Customers are impressed. Quote requests went up 40%.",
      metric: "40% increase in quotes"
    },
    {
      name: "Mike Hernandez",
      business: "Hardscape Specialists LLC",
      stars: 5,
      quote: "Storm cleanup is our bread and butter. Greet flags emergencies immediately, and we're always first to respond. We're winning jobs competitors miss because we answer instantly.",
      metric: "$50K+ in emergency work"
    }
  ];

  const pricingComparison = [
    { item: "Full-time receptionist salary", you: "$30,000/year", greet: "$0" },
    { item: "Part-time staff (20 hrs/week)", you: "$10,400/year", greet: "$0" },
    { item: "Phone system + features", you: "$60-100/month", greet: "Included" },
    { item: "After-hours answering service", you: "$200-400/month", greet: "Included" },
    { item: "Missed call revenue loss", you: "$10,000-50,000/year", greet: "$0" },
    { item: "GREET AI RECEPTIONIST", you: "-", greet: priceRangeDisplay }
  ];

  return (
    <LandingLayout>
      {/* Hero Section - Landscaper Focused */}
      <section className="relative bg-gradient-to-br from-emerald-900 via-green-900 to-emerald-950 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 via-green-500/10 to-teal-500/10"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-4xl">
            <div className="mb-6 inline-flex items-center gap-3 rounded-full bg-emerald-500/20 border border-emerald-400/30 px-4 py-2 text-sm font-semibold text-emerald-100">
              <span className="h-2 w-2 rounded-full bg-emerald-400"></span>
              Landscaping answering service · from {PUBLIC_SALE_STARTING_PRICE}
            </div>
            
            <h1 className="text-5xl md:text-7xl font-extrabold bg-gradient-to-r from-white via-emerald-100 to-green-200 bg-clip-text text-transparent pb-8 leading-tight">
              Never Miss a Landscaping Call Again
            </h1>
            
            <p className="text-xl md:text-2xl text-emerald-50 mb-4 leading-relaxed max-w-2xl">
              AI answering service that books consultations, captures emergency calls, and handles routine questions-cheaper than a $200–$400/mo answering service.
            </p>
            <p className="text-lg text-emerald-100/90 mb-8">
              Flat-rate from {PUBLIC_SALE_STARTING_PRICE} · No per-minute fees · 7-day free trial
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Link
                href="/try"
                onClick={() => trackButtonClick('try_demo', 'landscapers_hero')}
                className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white px-10 py-5 rounded-2xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-green-500/25 inline-flex items-center justify-center"
              >
                <PhoneIcon className="h-5 w-5 mr-2" />
                Hear it answer in 60 seconds
              </Link>
              <Link
                href={isAuthenticated ? '/app' : '/register'}
                onClick={() => trackButtonClick(isAuthenticated ? 'go_to_dashboard' : 'start_free_trial', 'landscapers_hero')}
                className="bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 px-10 py-5 rounded-2xl font-bold text-lg transition-all duration-300 inline-flex items-center justify-center hover:scale-105"
              >
                {isAuthenticated ? 'Go to Dashboard' : 'Start Free 7-Day Trial'}
                <ArrowRightIcon className="h-5 w-5 ml-2" />
              </Link>
            </div>
            <p className="mb-10 text-sm text-emerald-100/80">
              Or call a live demo:{' '}
              <a
                href="tel:+14158773166"
                onClick={() => trackPhoneClick('+14158773166', 'landscapers_hero_demo')}
                className="underline hover:text-white"
              >
                (415) 877-3166
              </a>
            </p>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6">
                <div className="text-3xl font-bold text-white mb-1">100%</div>
                <div className="text-sm text-emerald-100">Call Answer Rate</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6">
                <div className="text-3xl font-bold text-white mb-1">8-12 hrs</div>
                <div className="text-sm text-emerald-100">Weekly Time Saved</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6">
                <div className="text-3xl font-bold text-white mb-1">15 min</div>
                <div className="text-sm text-emerald-100">Setup Time</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6">
                <div className="text-3xl font-bold text-white mb-1">24/7</div>
                <div className="text-sm text-emerald-100">Availability</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points vs Solution */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Your Landscaping Business is Losing Money Every Day
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every missed call during spring/fall season is money walking out the door. Here's the real cost:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {painPoints.map((point, index) => (
              <div key={index} className="bg-red-50 border border-red-100 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="flex items-start mb-4">
                  <div className="flex-shrink-0">
                    <XCircleIcon className="h-8 w-8 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-bold text-red-900 mb-2">{point.problem}</h3>
                    <p className="text-red-700 font-semibold">{point.impact}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 bg-gradient-to-r from-emerald-50 to-green-50 rounded-3xl p-12 border-2 border-emerald-200">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Let's Do The Math</h3>
              <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                If you're a typical landscape company missing just 2-3 calls per week during busy season:
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-5xl font-bold text-emerald-600 mb-2">3</div>
                <div className="text-gray-700 font-semibold">Missed calls/week</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-green-600 mb-2">×$800</div>
                <div className="text-gray-700 font-semibold">Average job value</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-red-600 mb-2">$124,800</div>
                <div className="text-gray-700 font-semibold">Lost annually</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Greet: Built For Landscapers, By Landscapers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The AI receptionist designed to solve landscaping's #1 problem: never missing a call
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {landscaperBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              const colors = [
                'from-emerald-500 to-green-600',
                'from-green-500 to-teal-600',
                'from-teal-500 to-cyan-600',
                'from-cyan-500 to-blue-600',
                'from-blue-500 to-indigo-600',
                'from-indigo-500 to-purple-600'
              ];
              return (
                <div key={index} className="bg-white rounded-2xl border border-gray-100 p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${colors[index % colors.length]} flex items-center justify-center mb-6 shadow-lg transition-transform duration-300 group-hover:scale-110`}>
                    <Icon className="h-8 w-8 text-white transition-transform duration-300 group-hover:scale-125" />
                  </div>
                  <div className="inline-block px-4 py-1 bg-gray-100 rounded-full mb-4 transition-all duration-300 group-hover:bg-emerald-100 group-hover:scale-105">
                    <span className="text-2xl font-bold text-gray-900 transition-colors duration-300 group-hover:text-emerald-600">{benefit.stat}</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 transition-colors duration-300 group-hover:text-emerald-600">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed transition-colors duration-300 group-hover:text-gray-700">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works for Landscapers */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              How Greet Works For Landscapers
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="relative">
              <div className="bg-emerald-600 text-white w-14 h-14 rounded-full flex items-center justify-center text-2xl font-bold mb-6 shadow-lg">1</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Customer Calls</h3>
              <p className="text-gray-600">Someone calls your number about spring cleanup, a quote, or an emergency tree removal.</p>
            </div>

            <div className="relative">
              <div className="bg-emerald-600 text-white w-14 h-14 rounded-full flex items-center justify-center text-2xl font-bold mb-6 shadow-lg">2</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Greet Answers</h3>
              <p className="text-gray-600">AI picks up instantly with a friendly, professional voice and understands what they need.</p>
            </div>

            <div className="relative">
              <div className="bg-emerald-600 text-white w-14 h-14 rounded-full flex items-center justify-center text-2xl font-bold mb-6 shadow-lg">3</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Smart Handling</h3>
              <p className="text-gray-600">Greet answers questions, books consultations into your calendar, or flags emergencies for you.</p>
            </div>

            <div className="relative">
              <div className="bg-emerald-600 text-white w-14 h-14 rounded-full flex items-center justify-center text-2xl font-bold mb-6 shadow-lg">4</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">You Follow Up</h3>
              <p className="text-gray-600">Review complete call summaries and follow up with hot leads already pre-qualified.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Landscaper-Specific Use Cases */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Real Scenarios From Real Landscapers
            </h2>
          </div>

          <div className="space-y-8">
            {[
              {
                title: "Spring Rush: The Busiest Week of Your Year",
                scenario: "You're juggling 15 quote consultations while managing crews. Phone rings every 30 seconds.",
                old: "Missing 20% of calls, texting back hours later, losing jobs to competitors who answered",
                new: "Greet handles all routine calls, books consultations, only interrupts for high-priority inquiries. You respond to hot leads within minutes."
              },
              {
                title: "After-Hours Emergency Calls",
                scenario: "It's 10 PM. A customer's garage door is blocked by a fallen oak tree. They need help urgently.",
                old: "Either miss the call and lose emergency work, or get interrupted during family dinner",
                new: "Greet identifies it as emergency, gets details, alerts you immediately. You book high-paying emergency work without disrupting personal time"
              },
              {
                title: "The 'How Much Do You Charge?' Question",
                scenario: "You answer the same pricing question 40+ times per week during busy season.",
                old: "Wasting 3-4 hours weekly repeating the same information",
                new: "Greet answers instantly with your exact pricing, availability, and service areas. You only talk to serious prospects"
              },
              {
                title: "Weekend Calls from Quote-Shopping Customers",
                scenario: "Someone calls Saturday morning getting bids from 5 different companies.",
                old: "You're off, call goes to voicemail, they book with whoever answers",
                new: "Greet answers immediately, books their consultation for Monday, collects their info. You're in their inbox first"
              }
            ].map((scenario, index) => (
              <div key={index} className="bg-gradient-to-r from-gray-50 to-emerald-50 rounded-2xl p-8 border border-gray-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{scenario.title}</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-500 uppercase mb-2">Scenario</p>
                    <p className="text-gray-700">{scenario.scenario}</p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <XCircleIcon className="h-6 w-6 text-red-600" />
                        <p className="font-semibold text-gray-900">Without Greet</p>
                      </div>
                      <p className="text-gray-700">{scenario.old}</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <CheckCircleIcon className="h-6 w-6 text-green-600" />
                        <p className="font-semibold text-gray-900">With Greet</p>
                      </div>
                      <p className="text-gray-700">{scenario.new}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What Landscapers Are Saying
            </h2>
            <p className="text-xl text-gray-600">Join hundreds of landscape companies already capturing every call</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.stars)].map((_, i) => (
                    <StarIcon key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.quote}"</p>
                <div className="border-t border-gray-100 pt-6">
                  <div className="font-bold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600 mb-3">{testimonial.business}</div>
                  <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-sm font-semibold">
                    <CheckCircleIcon className="h-4 w-4" />
                    {testimonial.metric}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnsweringServiceVsGreet
        industryLabel="Landscapers"
        analyticsContext="landscapers_comparison"
        accent="emerald"
      />

      <section className="pb-8 bg-white">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-gray-600">
            Comparing phone coverage options?{' '}
            <Link href="/landscaping-answering-service" className="text-emerald-700 font-semibold hover:text-emerald-900">
              See our landscaping answering service guide
            </Link>
            {' '}or{' '}
            <Link href="/ai-receptionist-pricing" className="text-emerald-700 font-semibold hover:text-emerald-900">
              AI receptionist pricing
            </Link>
            .
          </p>
        </div>
      </section>

      {/* Pricing & ROI */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              The Cost of NOT Using Greet
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Here's what landscapers typically spend vs. Greet's pricing:
            </p>
          </div>

          <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-3xl p-12 border-2 border-emerald-200 overflow-hidden">
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {pricingComparison.slice(0, 3).map((item, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-sm">
                  <p className="text-gray-600 font-semibold mb-3">{item.item}</p>
                  <div className="text-2xl font-bold text-red-600 mb-2">{item.you}</div>
                  <div className="text-sm text-gray-500">Traditional approach</div>
                </div>
              ))}
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {pricingComparison.slice(3, 6).map((item, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-sm">
                  <p className="text-gray-600 font-semibold mb-3">{item.item}</p>
                  <div className="text-2xl font-bold text-green-600 mb-2">{item.greet}</div>
                  <div className="text-sm text-gray-500">With Greet</div>
                </div>
              ))}
            </div>

            <div className="text-center bg-white rounded-2xl p-12">
              <p className="text-gray-600 font-semibold mb-4">Annual Savings</p>
              <div className="text-6xl font-bold text-emerald-600 mb-4">$48,000+</div>
              <p className="text-xl text-gray-700">Plus the value of captured calls and your reclaimed time</p>
            </div>
          </div>

          <div className="mt-16 bg-gradient-to-r from-emerald-600 to-green-600 rounded-3xl p-12 text-center">
            <h3 className="text-3xl font-bold text-white mb-4">Greet Pricing for Landscapers</h3>
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              {PRICING_PLANS.map((plan) => {
                const displayPrice = getPlanDisplayPrice(plan, pricingContext);
                const isPopular = plan.id === 'growth';

                return (
                  <div
                    key={plan.id}
                    className={isPopular ? 'bg-white rounded-2xl p-8 ring-2 ring-emerald-300' : 'bg-white/10 rounded-2xl p-8 backdrop-blur-sm border border-white/20'}
                  >
                    {displayPrice.showSale && (
                      <div className={`text-xl font-semibold line-through mb-1 ${isPopular ? 'text-emerald-300' : 'text-emerald-100'}`}>
                        {displayPrice.basePrice.replace('/month', '')}
                      </div>
                    )}
                    <div className={`text-5xl font-bold mb-2 ${isPopular ? 'text-emerald-600' : 'text-white'}`}>
                      {displayPrice.price.replace('/month', '')}
                    </div>
                    <div className={`font-semibold ${isPopular ? 'text-emerald-700' : 'text-emerald-50'}`}>/month</div>
                    <div className={`text-sm mt-4 ${isPopular ? 'text-emerald-600' : 'text-emerald-100'}`}>
                      {isPopular ? 'Most Popular' : plan.name}
                    </div>
                    {displayPrice.showSale && displayPrice.label && (
                      <div className={`mt-3 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${isPopular ? 'bg-emerald-100 text-emerald-800' : 'bg-white/20 text-white'}`}>
                        {displayPrice.label}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-emerald-50">7-day free trial • Cancel anytime • No contracts</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Common Questions from Landscapers
            </h2>
          </div>

          <div className="space-y-4">
            {landscapersFaqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button
                  className="w-full px-6 py-5 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <ChevronDownIcon 
                    className={`h-5 w-5 text-gray-500 transform transition-transform ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div className={`px-6 text-gray-600 border-t border-gray-100 transition-all duration-300 ${
                  openFaq === index ? 'pb-5 max-h-96' : 'pb-0 max-h-0'
                } overflow-hidden`}>
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative bg-gradient-to-br from-emerald-900 via-green-900 to-emerald-950 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 via-green-500/10 to-teal-500/10"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 blur-3xl"></div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Stop Losing Money to Missed Calls
          </h2>
          <p className="text-xl text-emerald-50 mb-12 max-w-2xl mx-auto">
            Every day you wait costs you potential clients and revenue. Start answering 100% of calls today with your free 7-day trial.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link
              href="/try"
              onClick={() => trackButtonClick('try_demo', 'landscapers_cta_final')}
              className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white px-10 py-5 rounded-2xl font-bold text-lg transition-all duration-300 inline-flex items-center justify-center"
            >
              <PhoneIcon className="h-5 w-5 mr-2" />
              Hear it answer in 60 seconds
            </Link>
            <Link
              href={isAuthenticated ? '/app' : '/register'}
              onClick={() => trackButtonClick(isAuthenticated ? 'go_to_dashboard' : 'start_free_trial', 'landscapers_cta_final')}
              className="bg-white text-emerald-600 hover:bg-gray-50 px-10 py-5 rounded-2xl font-bold text-lg transition-all inline-flex items-center justify-center shadow-xl"
            >
              Start Your Free Trial
              <ArrowRightIcon className="h-5 w-5 ml-2" />
            </Link>
            <a
              href="tel:+14158773166"
              onClick={() => trackPhoneClick('+14158773166', 'landscapers_cta_final')}
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-10 py-5 rounded-2xl font-bold text-lg transition-all inline-flex items-center justify-center"
            >
              (415) 877-3166
            </a>
          </div>
          <p className="text-emerald-100 text-sm">
            ✓ Free 7-day trial • ✓ Set up in 15 minutes
          </p>
        </div>
      </section>
    </LandingLayout>
  );
}

